import * as React from 'react';
import { Box, Group, Stack, Switch, Text, UnstyledButton } from '@mantine/core';
import { type TrackReference, useLocalParticipant, useTrackToggle, VideoTrack } from '@livekit/components-react';
import { LocalVideoTrack, Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { type BackgroundEffect, BackgroundEffectProcessor } from './background/background-processor';
import { DeviceSelect } from './device-select';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';

const BACKGROUND_IMAGES = [
    { labelKey: 'camera.background.desk', path: '/background-images/samantha-gades-BlIhVfXbi9s-unsplash.jpg' },
    { labelKey: 'camera.background.nature', path: '/background-images/ali-kazal-tbw_KQE3Cbg-unsplash.jpg' },
] as const;

const BLUR_OPTIONS = [
    { labelKey: 'camera.background.blurLight', radius: 10 },
    { labelKey: 'camera.background.blurStrong', radius: 25 },
] as const;

type Background = { type: 'none' } | BackgroundEffect;

let rememberedBackground: Background = { type: 'none' };
let pendingBackground = Promise.resolve();

async function applyBackground(track: LocalVideoTrack, background: Background) {
    const current = track.getProcessor();
    if (background.type === 'none') {
        if (current) await track.stopProcessor();
        return;
    }
    if (current instanceof BackgroundEffectProcessor) {
        await current.setEffect(background);
        return;
    }
    const processor = new BackgroundEffectProcessor();
    await processor.setEffect(background);
    await track.setProcessor(processor);
}

function BackgroundOption({
    selected,
    label,
    image,
    blurred,
    onClick,
}: Readonly<{
    selected: boolean;
    label: string;
    image?: string;
    blurred?: boolean;
    onClick: () => void;
}>) {
    return (
        <UnstyledButton
            aria-pressed={selected}
            h="var(--spacing-14)"
            pos="relative"
            w="var(--spacing-20)"
            style={{
                overflow: 'hidden',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-default)',
                outline: selected ? 'var(--spacing-0-5) solid var(--color-action-default)' : undefined,
                outlineOffset: selected ? 'calc(var(--spacing-0-5) * -1)' : undefined,
                backgroundColor: 'var(--color-background-tertiary)',
                backgroundImage: image ? `url(${image})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
            onClick={onClick}
        >
            {blurred && (
                <Box
                    inset={0}
                    pos="absolute"
                    style={{
                        backgroundColor: 'var(--color-background-highlight)',
                        filter: 'blur(var(--spacing-2))',
                    }}
                />
            )}
            <Text
                bottom="var(--spacing-1)"
                left="var(--spacing-1)"
                pos="absolute"
                px="var(--spacing-1)"
                size="xs"
                style={{
                    backgroundColor: 'var(--color-background-default)',
                    borderRadius: 'var(--radius-sm)',
                }}
            >
                {label}
            </Text>
        </UnstyledButton>
    );
}

export function CameraSettings() {
    const { t } = useTranslation('settings');
    const { cameraTrack, localParticipant } = useLocalParticipant();
    const { enabled, pending, toggle } = useTrackToggle({ source: Track.Source.Camera });
    const [background, setBackground] = React.useState<Background>(rememberedBackground);

    const camTrackRef: TrackReference | undefined = React.useMemo(() => {
        return cameraTrack ? { participant: localParticipant, publication: cameraTrack, source: Track.Source.Camera } : undefined;
    }, [localParticipant, cameraTrack]);

    React.useEffect(() => {
        const track = cameraTrack?.track;
        if (!(track instanceof LocalVideoTrack)) return;
        rememberedBackground = background;
        pendingBackground = pendingBackground
            .then(() => applyBackground(track, background))
            .catch((error: unknown) => {
                notifyError(i18n.t('settings:camera.applyFailed'), error);
            });
    }, [cameraTrack, background]);

    return (
        <Stack gap="sm">
            {camTrackRef && (
                <VideoTrack
                    trackRef={camTrackRef}
                    style={{
                        maxHeight: 'var(--spacing-72)',
                        objectFit: 'contain',
                        objectPosition: 'right',
                        transform: 'scaleX(-1)',
                    }}
                />
            )}
            <Switch
                checked={enabled}
                disabled={pending}
                label={t('camera.toggle')}
                onChange={(event) => void toggle(event.currentTarget.checked)}
            />
            <DeviceSelect kind="videoinput" label={t('camera.device')} />
            <Stack gap="xs">
                <Text size="sm">{t('camera.backgroundEffects')}</Text>
                <Group gap="sm">
                    <BackgroundOption
                        label={t('camera.background.none')}
                        selected={background.type === 'none'}
                        onClick={() => {
                            setBackground({ type: 'none' });
                        }}
                    />
                    {BLUR_OPTIONS.map((option) => (
                        <BackgroundOption
                            key={option.radius}
                            blurred
                            label={t(option.labelKey)}
                            selected={background.type === 'blur' && background.radius === option.radius}
                            onClick={() => {
                                setBackground({ type: 'blur', radius: option.radius });
                            }}
                        />
                    ))}
                    {BACKGROUND_IMAGES.map((image) => (
                        <BackgroundOption
                            key={image.path}
                            image={image.path}
                            label={t(image.labelKey)}
                            selected={background.type === 'image' && background.path === image.path}
                            onClick={() => {
                                setBackground({ type: 'image', path: image.path });
                            }}
                        />
                    ))}
                </Group>
            </Stack>
        </Stack>
    );
}
